import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";

import medallionsImage from "assets/enhancements.svg";
import Damage from "components/base/Damage";
import Tooltip from "components/base/Tooltip";
import TooltipMedallions from "components/base/TooltipMedallions";
import {
  damagedAnimation,
  dodgeAnimation,
  escapeAnimation,
  floatAnimation,
  loseAnimation,
} from "components/styles/animations";
import { media } from "components/styles/breakpoints";
import { SKILL_SHIP_ANIMATIONS } from "components/styles/skills";
import AvatarShip from "components/ui/AvatarShip";
import { isFightModeStatus } from "libs/fight";
import { getSkillAnimation } from "libs/skill";
import { hasAnimation } from "libs/stats";
import { enterShip } from "redux/actions";
import {
  getBuildInfo,
  getInstalledUpgrades,
  getMedallions,
  getShipDisplayInfo,
  getUserInfo,
} from "redux/selectors/characterSelectors";
import {
  getCharacterDamage,
  getFightStatus,
} from "redux/selectors/fightSelectors";
import { COLORS } from "utils/constants";

interface AvatarWrapperProps {
  isMoving: boolean;
  isFighting: boolean;
  lastMove: string;
}

interface AvatarImageWrapperProps {
  animations: string[];
  lastMove: string;
}

const movingMixin = css<{ lastMove: string }>`
  margin-left: ${(props) => (props.lastMove === "right" ? "10%" : "-10%")};
  animation-play-state: paused;
`;

const AvatarWrapper = styled.div<AvatarWrapperProps>`
  height: 100%;
  aspect-ratio: 1/1;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${floatAnimation} 3.5s ease-in-out infinite;
  ${(props) => !!props.isMoving && movingMixin}
  transition: margin 800ms ease-in-out;
  ${(props) => !!props.isFighting && "pointer-events: none;"}

  &:hover {
    filter: brightness(120%);
  }
`;

const InfoWrapper = styled.div`
  position: absolute;
  bottom: 80%;
`;

const NameWrapper = styled.div`
  font-size: 14px;
  font-weight: 400;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 5px;
  gap: 6px;
  text-shadow: 0 0 3px #000000;

  ${media.desktop`
    font-size: 18px;
  `}
`;

const Name = styled.div`
  color: white;
`;

const MedallionsWrapper = styled.div`
  z-index: 2;
  position: relative;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const MedallionsInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
  color: ${COLORS.ENHANCEMENTS};
`;

const MedallionsIcon = styled.div<{ image: string }>`
  height: 16px;
  width: 16px;
  mask: url(${(props) => props.image}) no-repeat center;
  background-color: ${COLORS.ENHANCEMENTS};
`;

const MedallionsValue = styled.div``;

const damagedMixin = css`
  animation: ${damagedAnimation} 300ms ease-in-out forwards;
`;

const dodgeMixin = css`
  animation: ${dodgeAnimation} 300ms ease-in-out forwards;
`;

const escapeMixin = css`
  animation: ${escapeAnimation} 500ms ease-in forwards;
`;

const loseMixin = css`
  animation: ${loseAnimation} 3000ms ease-in forwards;
`;

const AvatarImageWrapper = styled.div<AvatarImageWrapperProps>`
  height: 100%;
  aspect-ratio: 1/1;
  transform: scaleX(${(props) => (props.lastMove === "right" ? 1 : -1)});
  ${(props) => (hasAnimation(props.animations, "damaged") ? damagedMixin : "")};
  ${(props) => (hasAnimation(props.animations, "dodge") ? dodgeMixin : "")};
  ${(props) => (hasAnimation(props.animations, "escape") ? escapeMixin : "")};
  ${(props) => (hasAnimation(props.animations, "lose") ? loseMixin : "")};
  ${(props) => getSkillAnimation(SKILL_SHIP_ANIMATIONS, props.animations)}
  cursor: pointer;
  position: relative;
`;

export default function Avatar() {
  const dispatch = useDispatch();

  const { isMoving, lastMove, animations } = useSelector(getShipDisplayInfo);
  const { userName } = useSelector(getUserInfo);
  const { ship } = useSelector(getBuildInfo);
  const installedUpgrades = useSelector(getInstalledUpgrades);
  const medallions = useSelector(getMedallions);
  const damage = useSelector(getCharacterDamage);
  const status = useSelector(getFightStatus);

  const isFightMode = isFightModeStatus(status);

  return (
    <AvatarWrapper
      isMoving={isMoving}
      isFighting={isFightMode}
      lastMove={lastMove}
      onClick={() => dispatch(enterShip())}
    >
      <InfoWrapper>
        <Damage damageList={damage} />
        <NameWrapper>
          <Name>{userName}</Name>
          <MedallionsWrapper>
            <Tooltip
              color={COLORS.ENHANCEMENTS}
              content={<TooltipMedallions />}
            >
              <MedallionsInfo>
                <MedallionsIcon image={medallionsImage} />
                <MedallionsValue>{medallions.total}</MedallionsValue>
              </MedallionsInfo>
            </Tooltip>
          </MedallionsWrapper>
        </NameWrapper>
      </InfoWrapper>
      <AvatarImageWrapper lastMove={lastMove} animations={animations}>
        <AvatarShip
          ship={ship}
          installedUpgrades={installedUpgrades}
          damage={damage}
          animations={animations}
          isMoving={isMoving}
        />
      </AvatarImageWrapper>
    </AvatarWrapper>
  );
}
